import L from 'leaflet';

export const pointStyles = {
  default: {
    radius: 5,
    fillColor: '#3388ff',
    color: '#fff',
    weight: 1,
    opacity: 1,
    fillOpacity: 0.8,
  },
  Destinations_Muster_Stations: {
    radius: 8,
    fillColor: '#ff7800',
    color: '#fff',
    weight: 2,
    opacity: 1,
    fillOpacity: 0.9,
  },
  RDCO_Evac_Zone_Centroids_v1_358: {
    radius: 6,
    fillColor: '#00ff00',
    color: '#000',
    weight: 1,
    opacity: 1,
    fillOpacity: 0.7,
  },
  addresses: {
    radius: 4,
    fillColor: '#ff0000',
    color: '#fff',
    weight: 1,
    opacity: 1,
    fillOpacity: 0.6,
  },
  CRD_PostSecondary_Schools_v1: {
    radius: 7,
    fillColor: '#8000ff',
    color: '#fff',
    weight: 2,
    opacity: 1,
    fillOpacity: 0.8,
  },
  CRD_Grade_Schools_v1: {
    radius: 6,
    fillColor: '#00ffff',
    color: '#000',
    weight: 2,
    opacity: 1,
    fillOpacity: 0.8,
  },
  CRD_Transit_Stops_v1: {
    radius: 5,
    fillColor: '#ffaa00',
    color: '#000',
    weight: 1,
    opacity: 1,
    fillOpacity: 0.7,
  },
};

export const lineStyles = {
  default: {
    color: '#000000ff',
    weight: 5,
    opacity: 0.8,
    dashArray: '10, 10',
  },
  gmap_route: {
    color: '#0000ffff',
    weight: 5,
    opacity: 0.9,
    dashArray: '',  // Primary route is solid (not dashed)
  },
  gmap_route_alternative: {
    color: '#0000ffff',
    weight: 3,
    opacity: 0.9,
    dashArray: '5, 5',  // Alternatives are dashed to differentiate
  },
  CRD_Transit_Routes_v1: {
    color: '#33aaffff',
    weight: 3,
    opacity: 0.6,
  },
  CRD_Model_Road_Network_v1: {
    color: '#d400ffff',
    weight: 3,
    opacity: 0.6,
  }
};

export const areaStyles = {
  default: {
    fillColor: '#3388ff',
    weight: 2,
    opacity: 1,
    color: '#3388ff',
    fillOpacity: 0.3,
  },
  RDCO_Evac_Zones_v1_358: {
    fillColor: '#ffff00',
    weight: 2,
    opacity: 1,
    color: '#ffff00',
    fillOpacity: 0.2,
  },
  CRD_TAZ_2025_v2: {
    fillColor: '#00ff00',
    weight: 1,
    opacity: 0.5,
    color: '#000',
    fillOpacity: 0.15,
  },
  CRD_Municipal_Boundaries_v1: {
    fillColor: '#888888',
    weight: 3,
    opacity: 1,
    color: '#ff0000ff',
    fillOpacity: 0.1,
  },
};

// Evacuation zone order-status colors (draft/no order keeps the default areaStyles color)
export const zoneStatusColors = {
  alerted: '#fc7100',
  committed: '#dc3545',
  rescinded: '#198754',
};

export const getZoneStatusStyle = (baseStyle, statusProps) => {
  if (!statusProps) return baseStyle;

  const { Is_Alerted, Is_Committed, Is_Rescinded } = statusProps;

  if (Is_Rescinded) {
    return { ...baseStyle, fillColor: zoneStatusColors.rescinded, color: zoneStatusColors.rescinded };
  }
  if (Is_Committed) {
    return { ...baseStyle, fillColor: zoneStatusColors.committed, color: zoneStatusColors.committed };
  }
  if (Is_Alerted) {
    return { ...baseStyle, fillColor: zoneStatusColors.alerted, color: zoneStatusColors.alerted };
  }
  return baseStyle;
};

export const customIcons = {
  musterStation: L.icon({
    iconUrl: '/assets/img/leaflet/muster_icon_50.png',
    iconSize: [25, 25],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  }),
  musterStationSelected: L.icon({
    iconUrl: '/assets/img/leaflet/muster_icon_50.png',
    iconSize: [50, 50],
    iconAnchor: [25, 25],
    popupAnchor: [0, -25],
  }),
};

// Muster station capacity donut chart colors (available vs. filled)
export const capacityColors = {
  available: zoneStatusColors.rescinded, // green
  filled: zoneStatusColors.committed, // red
};

// Builds an SVG donut chart: red arc = filled capacity, green arc = remaining capacity (TODO: Library)
export const createCapacityDonutSVG = (filledPercent, size = 90) => {
  const radius = 15.9155; // circumference = 100, so stroke-dasharray can use percentages directly
  const clampedFilled = Math.max(0, Math.min(100, filledPercent));
  const remaining = 100 - clampedFilled;

  return `
    <svg width="${size}" height="${size}" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r="${radius}" fill="none" stroke="${capacityColors.available}" stroke-width="4"></circle>
      <circle cx="18" cy="18" r="${radius}" fill="none" stroke="${capacityColors.filled}" stroke-width="4"
        stroke-dasharray="${clampedFilled} ${remaining}" stroke-dashoffset="25" transform="rotate(-90 18 18)"></circle>
      <text x="18" y="20.5" text-anchor="middle" font-size="7" font-weight="bold" fill="#212529">${Math.round(clampedFilled)}%</text>
    </svg>
  `;
};

// Builds hover popup content for a muster station: name/address + capacity donut chart (TODO: Library)
export const createMusterStationPopupContent = (properties) => {
  const name = properties?.['Building Name']?.trim() || properties?.Dest_Name || 'Muster Station';
  const address = properties?.Address || properties?.Dest_Address || '';
  const capacity = properties?.Capacity;
  const occupancy = properties?.Current_Occupancy;

  let bodyContent;
  if (capacity === null || capacity === undefined || Number(capacity) <= 0) {
    bodyContent = '<div style="color: #6c757d; font-size: 0.85rem;">Capacity data not available</div>';
  } else {
    const filledPercent = (Number(occupancy || 0) / Number(capacity)) * 100;
    bodyContent = `
      <div style="display: flex; align-items: center; gap: 10px;">
        ${createCapacityDonutSVG(filledPercent)}
        <div style="font-size: 0.85rem;">
          <div><span style="color: ${capacityColors.filled};">&#9632;</span> Filled: ${Number(occupancy || 0)}</div>
          <div><span style="color: ${capacityColors.available};">&#9632;</span> Available: ${Math.max(0, Number(capacity) - Number(occupancy || 0))}</div>
          <div style="margin-top: 4px;"><strong>Capacity:</strong> ${Number(capacity)}</div>
        </div>
      </div>
    `;
  }

  return `
    <div style="padding: 5px; min-width: 220px;">
      <div style="font-weight: bold; margin-bottom: 4px;">${name}</div>
      ${address ? `<div style="font-size: 0.8rem; color: #6c757d; margin-bottom: 6px;">${address}</div>` : ''}
      ${bodyContent}
    </div>
  `;
};


export const getStyle = (layerType, layerName) => {
  if (layerType === 'point') {
    return pointStyles[layerName] || pointStyles.default;
  } else if (layerType === 'line') {
    // Check for exact match first
    if (lineStyles[layerName]) {
      return lineStyles[layerName];
    }
    // Check for pattern match: alternative routes like 'gmap_route_alternative_1', 'gmap_route_alternative_2'
    if (layerName && layerName.startsWith('gmap_route_alternative')) {
      return lineStyles.gmap_route_alternative;
    }
    return lineStyles.default;
  } else {
    return areaStyles[layerName] || areaStyles.default;
  }
};

export const getSelectionStyle = (layerType, originalStyle) => {
  if (layerType === 'point') {
    return {
      fillColor: '#ff0000',
      color: '#ff0000',
      weight: originalStyle.weight + 1,
      fillOpacity: 0.9,
      opacity: 1,
      radius: originalStyle.radius + 2,
    };
  } else if (layerType === 'line') {
    return {
      color: '#ff0000',
      weight: originalStyle.weight + 2,
      opacity: 1,
    };
  } else {
    return {
      color: '#ff0000ff',
      fillColor: '#0dff00ff',
      weight: originalStyle.weight + 6,
      fillOpacity: originalStyle.fillOpacity,
      opacity: 1,
    };
  }
};

export const getHoverStyle = (layerType, originalStyle) => {
  if (layerType === 'point') {
    return {
      fillColor: '#ff0000',
      color: '#ff0000',
      weight: originalStyle.weight + 1,
      fillOpacity: 0.9,
      opacity: 1,
      radius: originalStyle.radius + 2,
    };
  } else if (layerType === 'line') {
    return {
      color: '#ff0000',
      weight: originalStyle.weight + 2,
      opacity: 1,
    };
  } else {
    return {
      color: '#ff0000',
      fillColor: '#ff0000',
      weight: originalStyle.weight + 2,
      fillOpacity: Math.min((originalStyle.fillOpacity || 0.3) + 0.2, 1),
      opacity: 1,
    };
  }
};